/* eslint-disable @typescript-eslint/no-explicit-any */

import { ActionTemplateParameterResource, NamedResource, NewSpaceScopedResource, ResourceWithLinks, SensitiveValue, SpaceScopedResource, TenantedDeploymentMode } from "client/resources";
import { DeploymentActionPackageResource } from "./deploymentActionPackageResource";
import { MetadataTypeCollection } from "./dynamicFormResources";
import { ExtensionSettingsValues } from "./extensionSettingsValues";
import { GitRef } from "./versionControlledResource";

interface BaseProjectResourceLinks {
    Self: string;
    Releases: string;
    Channels: string;
    Triggers: string;
    ScheduledTriggers: string;
    OrderChannels: string;
    Variables: string;
    Progression: string;
    RunbookTaskRunDashboardItemsTemplate: string;
    DeploymentProcess: string;
    Web: string;
    Logo: string;
    Metadata: string;
    Runbooks: string;
    RunbookSnapshots: string;
    Summary: string;
}

type VcsProjectResourceLinks = BaseProjectResourceLinks & {
    Branches: string;
};

type DatabaseProjectResourceLinks = BaseProjectResourceLinks & {
    ConvertToVcs: string;
};

type ProjectResourceLinks = VcsProjectResourceLinks | DatabaseProjectResourceLinks;

export enum PersistenceSettingsType {
    VersionControlled = "VersionControlled",
    Database = "Database",
}

interface DatabasePersistenceSettings {
    Type: PersistenceSettingsType.Database;
}

export interface VersionControlledPersistenceSettings {
    Type: PersistenceSettingsType.VersionControlled;
    Credentials: AnonymousVcsCredentials | UsernamePasswordVcsCredentials;
    Url: string;
    DefaultBranch: string;
    BasePath: string;
}

export enum AuthenticationType {
    Anonymous = "Anonymous",
    UsernamePassword = "UsernamePassword",
}

export interface UsernamePasswordVcsCredentials {
    Type: AuthenticationType.UsernamePassword;
    Username: string;
    Password: SensitiveValue;
}

export interface AnonymousVcsCredentials {
    Type: AuthenticationType.Anonymous;
}

export function IsUsingUsernamePasswordAuth(T: AnonymousVcsCredentials | UsernamePasswordVcsCredentials): T is UsernamePasswordVcsCredentials {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (T as UsernamePasswordVcsCredentials).Type === AuthenticationType.UsernamePassword;
}

export function HasVcsProjectResourceLinks(links: ProjectResourceLinks): links is VcsProjectResourceLinks {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (links as VcsProjectResourceLinks).Branches !== undefined;
}

export function HasVersionControlledPersistenceSettings(T: VersionControlledPersistenceSettings | DatabasePersistenceSettings): T is VersionControlledPersistenceSettings {
    return T.Type === PersistenceSettingsType.VersionControlled;
}

export interface ProjectResource extends NamedResource<ProjectResourceLinks>, SpaceScopedResource {
    VariableSetId: string;
    DeploymentProcessId: string;
    DiscreteChannelRelease: boolean;
    IncludedLibraryVariableSetIds: string[];
    DefaultToSkipIfAlreadyInstalled: boolean;
    TenantedDeploymentMode: TenantedDeploymentMode;
    VersioningStrategy: VersioningStrategy;
    ReleaseCreationStrategy: ReleaseCreationStrategy;
    Templates: ActionTemplateParameterResource[];
    AutoDeployReleaseOverrides: any[];
    LifecycleId: string;
    AutoCreateRelease: boolean;
    ProjectConnectivityPolicy: ProjectConnectivityPolicy;
    DefaultGuidedFailureMode: GuidedFailureMode;
    ClonedFromProjectId: string;
    ExtensionSettings: ExtensionSettingsValues[];
    ReleaseNotesTemplate: string;
    DeploymentChangesTemplate: string;
    IsVersionControlled: boolean;
    PersistenceSettings: VersionControlledPersistenceSettings | DatabasePersistenceSettings;
    Slug: string;
    ProjectGroupId: string;
    Description: string;
    IsDisabled: boolean;
}

export type ProjectOrSummaryResource = ProjectResource | ProjectSummaryResource;

export interface ProjectSummaryResource extends NamedResource<BaseProjectResourceLinks>, SpaceScopedResource {
    Slug: string;
    ProjectGroupId: string;
    Description: string;
    IsDisabled: boolean;
}

export interface NewProjectResource extends NewSpaceScopedResource {
    Name: string;
    Description: string;
    ProjectGroupId: string;
    LifecycleId: string;
}

export interface ProjectSettingsMetadata {
    ExtensionId: string;
    Metadata: MetadataTypeCollection;
}

export interface VersioningStrategy {
    Template: string;
    DonorPackage?: DeploymentActionPackageResource;
    DonorPackageStepId?: string;
}

export interface ReleaseCreationStrategy {
    ReleaseCreationPackage: DeploymentActionPackageResource;
    ChannelId?: string;
    ReleaseCreationPackageStepId?: string;
}

export interface ProjectConnectivityPolicy {
    SkipMachineBehavior: string;
    TargetRoles: string[];
    AllowDeploymentsToNoTargets: boolean;
    ExcludeUnhealthyTargets: boolean;
}

export interface VersionControlCompatibilityResponse {
    Errors: string[];
    Warnings: string[];
    Notices: string[];
}

export interface ConvertProjectToVersionControlledResponse {
    Messages: string[];
}

export enum GuidedFailureMode {
    EnvironmentDefault = "EnvironmentDefault",
    Off = "Off",
    On = "On",
}

export interface RecentlyViewedProjectIds {
    [key: string]: string[];
}

export interface ProjectSummary {
    HasRunbooks: boolean;
    HasDeploymentProcess: boolean;
}

type VcsBranchResourceLinks = {
    DeploymentProcess: string;
    Runbook: string;
    ReleaseTemplate: string;
};

export interface VcsBranchResource extends ResourceWithLinks<VcsBranchResourceLinks> {
    Name: GitRef;
}

export default ProjectResource;
